import type { GenerationView, KbDocument } from "@cosmosai/api-client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteDocument, useRetry } from "../../api/knowledge-queries";
import { useFormat } from "../../i18n/format";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/confirm-dialog";
import { CopyButton } from "../../ui/copy-button";
import { KeyValue } from "../../ui/key-value";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../ui/sheet";
import { toast } from "../../ui/sonner";
import { problemText } from "./problems";
import { INGESTION_VARIANT } from "./status";

/** One document: its facts, and its ingestion in every generation, with retry and delete. */
export function DocumentSheet({
  workspace,
  project,
  kb,
  document,
  generations,
  onClose,
}: {
  workspace: string;
  project: string;
  kb: string;
  document: KbDocument | undefined;
  generations: GenerationView[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { bytes, dateTime, decimal, number } = useFormat();
  const retry = useRetry(workspace, project, kb);
  const remove = useDeleteDocument(workspace, project, kb);
  const [confirming, setConfirming] = useState(false);
  const servedBy = (generationId: string) =>
    generations.find((generation) => generation.id === generationId)?.environments ?? [];
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pr-14">
          <div>
            <SheetTitle className="[overflow-wrap:anywhere]">
              {document?.name ?? t("knowledge.sheet.gone")}
            </SheetTitle>
            <SheetDescription>{t("knowledge.sheet.description")}</SheetDescription>
          </div>
          {document && (
            <div className="space-y-5">
              <KeyValue
                items={[
                  { term: t("knowledge.columns.type"), value: document.mediaType ?? "—" },
                  { term: t("knowledge.columns.size"), value: bytes(document.sizeBytes) },
                  { term: t("knowledge.columns.added"), value: dateTime(document.createdAt) },
                  {
                    term: "SHA-256",
                    value: (
                      <span className="inline-flex items-center gap-1">
                        <code className="text-sm">{document.sha256.slice(0, 16)}…</code>
                        <CopyButton value={document.sha256} label={t("knowledge.sheet.copySha")} />
                      </span>
                    ),
                  },
                ]}
              />
              <section aria-label={t("knowledge.sheet.ingestions")} className="space-y-3">
                <h3 className="font-semibold">{t("knowledge.sheet.ingestions")}</h3>
                {document.ingestions.map((ingestion) => (
                  <div key={ingestion.generationId} className="rounded-md border border-line p-3">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <code className="text-sm">
                        {ingestion.digest.replace("sha256:", "").slice(0, 12)}
                      </code>
                      <Badge variant={INGESTION_VARIANT[ingestion.status]}>
                        {t(`knowledge.status.${ingestion.status}`)}
                      </Badge>
                      <span className="text-sm text-ink-3">
                        {servedBy(ingestion.generationId).join(", ") ||
                          t("knowledge.sheet.servedNowhere")}
                      </span>
                    </div>
                    <KeyValue
                      items={[
                        { term: t("knowledge.sheet.attempts"), value: number(ingestion.attempts) },
                        {
                          term: t("knowledge.sheet.pages"),
                          value: ingestion.pages == null ? "—" : number(ingestion.pages),
                        },
                        {
                          term: t("knowledge.sheet.chunks"),
                          value: ingestion.chunks == null ? "—" : number(ingestion.chunks),
                        },
                        {
                          term: t("knowledge.sheet.tokens"),
                          value: ingestion.tokens == null ? "—" : number(ingestion.tokens),
                        },
                        {
                          term: t("knowledge.sheet.cost"),
                          value: ingestion.cost ? decimal(ingestion.cost) : "—",
                        },
                        ...(ingestion.errorCode
                          ? [
                              {
                                term: t("knowledge.sheet.error"),
                                value: (
                                  <span>
                                    <code className="text-sm">{ingestion.errorCode}</code>{" "}
                                    {ingestion.errorMessage}
                                  </span>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </div>
                ))}
              </section>
              <div className="flex flex-wrap gap-2">
                {document.ingestions.some((ingestion) => ingestion.status === "failed") && (
                  <Button
                    variant="outline"
                    disabled={retry.isPending}
                    onClick={() =>
                      retry.mutate(document.id, {
                        onSuccess: (answer) =>
                          toast.success(t("knowledge.retried", { count: answer.queued })),
                        onError: (error) => toast.error(problemText(t, error)),
                      })
                    }
                  >
                    {t("knowledge.sheet.retry")}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  disabled={document.status === "deleting"}
                  onClick={() => setConfirming(true)}
                >
                  {document.status === "deleting"
                    ? t("knowledge.status.deleting")
                    : t("knowledge.sheet.delete")}
                </Button>
              </div>
            </div>
          )}
          {confirming && document && (
            <ConfirmDialog
              title={t("knowledge.sheet.deleteTitle")}
              description={t("knowledge.sheet.deleteText", { name: document.name })}
              confirmLabel={t("knowledge.sheet.delete")}
              destructive
              pending={remove.isPending}
              error={remove.error}
              onConfirm={() =>
                remove.mutate(document.id, {
                  onSuccess: () => {
                    setConfirming(false);
                    onClose();
                  },
                })
              }
              onClose={() => setConfirming(false)}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
